import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Inbox } from "lucide-react";

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return s;
  }
};

export const AdminEnquiries = ({ client, api, onCount }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${api}/enquiries`);
      setLeads(data);
      onCount?.(data.length);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  }, [client, api, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="enquiries-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">
          Enquiries ({leads.length}) {loading && <Loader2 size={12} className="inline animate-spin ml-2" />}
        </p>
        <button data-testid="leads-refresh-button" onClick={load} className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 flex items-center gap-2">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {leads.length === 0 && !loading ? (
        <div data-testid="leads-empty" className="border border-white/10 bg-steel p-14 text-center">
          <Inbox size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">No enquiries yet</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-steel overflow-x-auto">
          <table data-testid="leads-table" className="w-full min-w-[900px]">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Name</th>
                <th className="py-3 px-4 font-medium">Phone</th>
                <th className="py-3 px-4 font-medium">Email</th>
                <th className="py-3 px-4 font-medium">Product</th>
                <th className="py-3 px-4 font-medium">Qty</th>
                <th className="py-3 px-4 font-medium">Message</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} data-testid={`lead-row-${lead.id.slice(0, 8)}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300 align-top">
                  <td className="py-4 px-4 font-mono text-xs text-smoke whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                  <td className="py-4 px-4 font-heading font-semibold text-base uppercase tracking-wide text-white whitespace-nowrap">{lead.name}</td>
                  <td className="py-4 px-4">
                    <a href={`tel:${lead.phone}`} className="font-mono text-sm text-white hover:text-smoke transition-colors duration-300 whitespace-nowrap">{lead.phone}</a>
                  </td>
                  <td className="py-4 px-4 font-mono text-xs text-smoke">{lead.email || "—"}</td>
                  <td className="py-4 px-4 font-mono text-xs text-white whitespace-nowrap">{lead.product}</td>
                  <td className="py-4 px-4 font-mono text-xs text-smoke">{lead.quantity || "—"}</td>
                  <td className="py-4 px-4 font-body text-sm text-smoke max-w-xs">{lead.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
